import { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { getChildrenFromEvent, type EventRow } from '@/lib/schedule';
import type { SpanBarPlacement, OngoingTint } from '@/lib/ongoing';

export interface SpanBarGroupProps {
  /** Bars covering the browsed week, in section order. */
  bars: SpanBarPlacement[];
  onSelect: (event: EventRow) => void;
  className?: string;
}

/** Bars visible before the overflow action has to offer the rest. */
const COLLAPSED_LIMIT = 2;

const DAYS_IN_WEEK = 7;

/**
 * Bar fills per AC 13. Computed against TODAY, never the browsed week: a
 * March event seen from a March-week view is not urgent, and painting it red
 * would teach the parent to ignore the colour.
 */
const TINT_CLASS: Record<OngoingTint, string> = {
  error: 'bg-danger-light border-danger-border text-text-error',
  warning: 'bg-warning-light border-warning-border text-warning',
  neutral: 'bg-surface-secondary border-border text-text-body',
};

/**
 * The span bars pinned under the week strip (YAR-419 ACs 10-15).
 *
 * Each bar is laid on the same seven columns as the day cells above it, so its
 * left and right edges line up with the days the event actually covers. A
 * squared-off edge means the run continues past the week in that direction —
 * which is the only cue distinguishing "ends Saturday" from "still going next
 * week", and the reason the bars are grid-placed rather than fixed-width.
 */
export function SpanBarGroup({
  bars,
  onSelect,
  className = '',
}: SpanBarGroupProps) {
  const [expanded, setExpanded] = useState(false);

  // Expanding the pinned group is a peek, not a mode: it folds back as soon as
  // the parent scrolls on (AC 15).
  //
  // The listener lives here, and only while expanded, on purpose. Driving this
  // from the Schedule tab meant a `setState` on every scroll event — a
  // re-render of the whole feed per scroll frame, to collapse something that
  // is closed almost all of the time.
  useEffect(() => {
    if (!expanded) return;
    const collapse = () => setExpanded(false);
    window.addEventListener('scroll', collapse, { passive: true });
    return () => window.removeEventListener('scroll', collapse);
  }, [expanded]);

  // Nothing covers this week: the strip disappears entirely — no empty
  // container and no divider, which would otherwise read as a rule under the
  // calendar on most weeks of the year (AC 10).
  if (bars.length === 0) return null;

  const total = bars.length;
  const hasOverflow = total > COLLAPSED_LIMIT;
  const visible = expanded ? bars : bars.slice(0, COLLAPSED_LIMIT);

  return (
    <div
      className={['flex flex-col gap-1.5 w-full pb-2', className]
        .filter(Boolean)
        .join(' ')}
    >
      <div
        className="grid gap-x-0 gap-y-1.5"
        style={{
          gridTemplateColumns: `repeat(${DAYS_IN_WEEK}, minmax(0, 1fr))`,
        }}
      >
        {visible.map((bar, i) => (
          <SpanBar
            key={bar.ongoing.event.id}
            bar={bar}
            row={i + 1}
            onSelect={onSelect}
          />
        ))}
      </div>

      {hasOverflow && (
        <div className="flex w-full items-center justify-center">
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="inline-flex items-center justify-center gap-1.5 rounded-md px-1.5 py-1 text-sm font-semibold text-muted-foreground hover:bg-muted transition-colors duration-150 cursor-pointer"
          >
            {expanded ? 'Show less' : `See all (${total})`}
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      )}
    </div>
  );
}

function SpanBar({
  bar,
  row,
  onSelect,
}: {
  bar: SpanBarPlacement;
  /** 1-based grid row. Explicit because auto-placement would pack two bars
   *  with non-overlapping columns onto one line, reading as a single event. */
  row: number;
  onSelect: (event: EventRow) => void;
}) {
  const { ongoing, startCol, endCol, continuesLeft, continuesRight } = bar;
  const children = getChildrenFromEvent(ongoing.event);
  const [first, ...rest] = children;

  return (
    <button
      type="button"
      data-testid="span-bar"
      onClick={() => onSelect(ongoing.event)}
      // The accessible name has to carry what the bar's geometry says
      // visually — a screen reader gets no edge shape.
      aria-label={`${ongoing.event.title} — ${ongoing.label}`}
      style={{ gridColumn: `${startCol + 1} / ${endCol + 2}`, gridRow: row }}
      className={[
        'flex min-w-0 items-center gap-2 overflow-hidden border py-1 pl-3 pr-2 cursor-pointer text-left',
        // A rounded edge starts or ends inside this week; a square one runs on.
        continuesLeft ? '' : 'rounded-l-full',
        continuesRight ? '' : 'rounded-r-full',
        TINT_CLASS[ongoing.tint],
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span className="flex-1 min-w-0 truncate text-sm font-medium">
        {ongoing.event.title}
      </span>
      {first && (
        <span className="flex shrink-0 items-center gap-0.5">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full text-xs">
            {first.emoji_avatar ?? '🧒'}
          </span>
          {rest.length > 0 && (
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-border bg-background text-[10px] leading-3 text-text-body">
              +{rest.length}
            </span>
          )}
        </span>
      )}
    </button>
  );
}
